import React, { useContext } from 'react';
import {
    BarElement,
    CategoryScale,
    Chart as ChartJS,
    Legend,
    LinearScale,
    Title,
    Tooltip
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Bar } from 'react-chartjs-2';
import { Colors } from '../../../Utils/styles/Colors';
import { I18nReactContext } from '../../../contexts/I18nContext';
import './BarChart.scss';

// Register Chart.js components
ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ChartDataLabels
);

/**
 * Bar chart component using Chart.js for displaying question response data
 * Follows WeezBee's chart patterns with integrated color system and Hebrew support
 * 
 * @param {object} params - Component parameters
 * @param {Array} params.data - Chart data array with labels and values
 * @param {string} params.questionText - The survey question text to display as title
 * @param {boolean} params.responsive - Whether the chart should be responsive (default: true)
 * @param {number} params.height - Chart height in pixels (default: 300)
 * @param {boolean} params.showDataLabels - Whether to show data labels on bars (default: true)
 * @returns {JSX.Element} Rendered bar chart component
 */
export const BarChart = ({ 
    data, 
    questionText, 
    responsive = true, 
    height = 300,
    showDataLabels = true,
}) => {
    const { i18n } = useContext(I18nReactContext);

    // Consistent WeezBee color palette
    const colorPalette = [
        Colors["$GraphBackground2"],
    ];

    // Calculate total for percentage calculations
    const total = data.reduce((sum, item) => sum + item.value, 0);

    // Calculate percentages for each data point
    const dataWithPercentages = data.map(item => ({
        ...item,
        percentage: total > 0 ? ((item.value / total) * 100).toFixed(1) : 0
    }));

    // Prepare data for Chart.js
    const chartData = {
        labels: data.map(item => item.label) || [],
        datasets: [
            {
                label: questionText || i18n.translate('surveyCharts.tooltips.responses'),
                data: data.map(item => item.value) || [],
                backgroundColor: colorPalette,
                borderColor: Colors["$GraphBorder"],
                borderWidth: 1,
                borderRadius: 4,
                borderSkipped: false,
            }
        ]
    };

    // Chart.js options following WeezBee patterns
    const options = {
        responsive: responsive,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false // Hide legend for single dataset
            },
            tooltip: {
                enabled: true,
                backgroundColor: Colors["$themeLight"],
                titleColor: Colors["$pageTitle"],
                bodyColor: Colors["$pageTitle"],
                borderColor: Colors["$GraphBorder"],
                borderWidth: 1,
                cornerRadius: 6,
                displayColors: true,
                titleFont: {
                    family: 'Heebo',
                    size: 14,
                    weight: 'bold'
                },
                bodyFont: {
                    family: 'Heebo',
                    size: 13
                },
                callbacks: {
                    title: function(context) {
                        return context[0]?.label || '';
                    },
                    label: function(context) {
                        const value = context.raw || context.parsed;
                        const percentage = dataWithPercentages[context.dataIndex]?.percentage || 0;
                        const label = context.label || '';
                        return `${label}: ${value} ${i18n.translate('surveyCharts.tooltips.responses')} (${percentage}%)`;
                    }
                }
            },
            datalabels: {
                display: showDataLabels,
                anchor: 'end',
                align: function(context) {
                    // Safety checks for context and parsed data
                    if (!context || !context.chart || !context.chart.data || !context.chart.data.datasets || !context.parsed) {
                        return 'top'; // Default position
                    }
                    
                    try {
                        // Get the maximum value in the dataset
                        const dataset = context.chart.data.datasets[0];
                        if (!dataset || !dataset.data || dataset.data.length === 0) {
                            return 'top';
                        }
                        
                        const maxValue = Math.max(...dataset.data);
                        const currentValue = context.parsed.y;
                        
                        // If the current value is close to the max (within 10%), position inside the bar
                        if (currentValue >= maxValue * 0.9) {
                            return 'start'; // Position inside the bar
                        }
                        return 'top'; // Position above the bar
                    } catch (error) {
                        console.warn('Error in datalabels align function:', error);
                        return 'top'; // Default fallback
                    }
                },
                color: function(context) {
                    // Safety checks for context and parsed data
                    if (!context || !context.chart || !context.chart.data || !context.chart.data.datasets || !context.parsed) {
                        return Colors["$pageTitle"]; // Default color
                    }
                    
                    try {
                        // Get the maximum value in the dataset
                        const dataset = context.chart.data.datasets[0];
                        if (!dataset || !dataset.data || dataset.data.length === 0) {
                            return Colors["$pageTitle"];
                        }
                        
                        const maxValue = Math.max(...dataset.data);
                        const currentValue = context.parsed.y;
                        
                        // Use white text when inside the bar, default color when outside
                        if (currentValue >= maxValue * 0.9) {
                            return Colors["$pageTitle"];
                        }
                        return Colors["$pageTitle"];
                    } catch (error) {
                        console.warn('Error in datalabels color function:', error);
                        return Colors["$pageTitle"]; // Default fallback
                    }
                },
                font: {
                    family: 'Heebo',
                    size: 12,
                    weight: 'bold'
                },
                formatter: (value) => {
                    return value > 0 ? `${value}` : '';
                }
            }
        },
        scales: {
            x: {
                grid: {
                    display: false
                },
                ticks: {
                    color: Colors["$pageTitle"],
                    font: {
                        family: 'Heebo',
                        size: 12
                    },
                    maxRotation: 45,
                    minRotation: 0
                }
            },
            y: {
                beginAtZero: true,
                grid: {
                    color: Colors["$lightGrey"],
                    drawBorder: false
                },
                ticks: {
                    color: Colors["$pageTitle"],
                    font: {
                        family: 'Heebo',
                        size: 12
                    },
                    precision: 0, // Show only whole numbers
                    callback: function(value) {
                        return Number.isInteger(value) ? value : '';
                    }
                }
            }
        },
        layout: {
            padding: {
                top: showDataLabels ? 30 : 10,
                bottom: 10,
                left: 10,
                right: 10
            }
        }
    };

    return (
        <div className="bar-chart-container">
            <div 
                className="bar-chart-container__chart-wrapper" 
                style={{ height: `${height}px` }}
            >
                <Bar 
                    data={chartData} 
                    options={options} 
                    plugins={showDataLabels ? [ChartDataLabels] : []}
                />
            </div>
        </div>
    );
}; 